import type { PostHog } from "posthog-js";
import { analyticsEnvironment, analyticsPath, POSTHOG, POSTHOG_OPTIONS } from "../../../config/posthog.ts";

declare const __ZOOI_POSTHOG_VALIDATION__: boolean;

let loading: Promise<PostHog | null> | undefined;
let currentUser: string | null = null;
let lastVisit: string | null = null;
let opened = false;
let generation = 0;

function sdk() {
  if (loading) return loading;
  const environment = analyticsEnvironment(window.location.hostname, __ZOOI_POSTHOG_VALIDATION__);
  if (!environment || !POSTHOG.project_token) return Promise.resolve(null);
  loading = import("posthog-js/no-external").then(({ posthog }) => {
    posthog.init(POSTHOG.project_token, POSTHOG_OPTIONS);
    return posthog;
  }).catch(() => null);
  return loading;
}

/** Called only after /api/me succeeds; no email, names or workspace content enter analytics. */
export function trackAppVisit(userId: string, pathname: string, view?: unknown, layout?: "list" | "timeline") {
  const path = analyticsPath("app", pathname);
  const visit = `${userId}:${pathname}:${typeof view === "string" ? view : ""}:${layout ?? ""}`;
  if (!path || visit === lastVisit) return;
  lastVisit = visit;
  const expected = generation;
  void sdk().then((posthog) => {
    if (!posthog || generation !== expected) return;
    const properties = { surface: "app", environment: analyticsEnvironment(window.location.hostname, __ZOOI_POSTHOG_VALIDATION__), $pathname: path, view, layout };
    // Identify with our opaque account ID; the cross-subdomain cookie links the
    // anonymous website visit. Person properties remain empty.
    const previousUser = posthog.get_property("$user_id");
    if (typeof previousUser === "string" && previousUser !== userId) { posthog.reset(); opened = false; }
    posthog.register(properties);
    if (currentUser !== userId) { posthog.identify(userId); currentUser = userId; }
    if (!opened) { posthog.capture("app_opened", properties); opened = true; }
    posthog.capture("$pageview", properties);
  });
}

export function resetAnalytics() {
  generation++;
  currentUser = null;
  lastVisit = null;
  opened = false;
  void loading?.then((posthog) => posthog?.reset());
}
